/**
 * Migration Verification Utility
 * Run this from your browser console to verify the customer migration was successful
 */

import { directSupabase } from '../lib/supabaseClient';

export async function verifyCustomerMigration() {
  console.log('🔍 Starting migration verification...');
  
  try {
    // 1. Check customer_migration_mapping table
    console.log('📊 Checking migration mapping table...');
    const { data: mappings, error: mappingError } = await directSupabase
      .from('customer_migration_mapping')
      .select('*')
      .limit(10);
    
    if (mappingError) {
      console.error('❌ Error fetching migration mappings:', mappingError);
    } else {
      console.log(`✅ Found ${mappings?.length || 0} migration mapping records (showing first 10)`);
      console.table(mappings);
    }

    // 2. Check total customers in local database
    console.log('📊 Checking local customer count...');
    const { count: customerCount, error: customerError } = await directSupabase
      .from('customers')
      .select('*', { count: 'exact', head: true });
    
    if (customerError) {
      console.error('❌ Error counting customers:', customerError);
    } else {
      console.log(`✅ Total customers in JARVIS database: ${customerCount}`);
    }

    // 3. Check projects with customer relationships
    console.log('📊 Checking project-customer relationships...');
    const { data: projects, error: projectError } = await directSupabase
      .from('projects')
      .select('id, project_name, customer_id')
      .not('customer_id', 'is', null)
      .limit(10);
    
    if (projectError) {
      console.error('❌ Error fetching projects:', projectError);
    } else {
      console.log(`✅ Found ${projects?.length || 0} projects with customer relationships (showing first 10)`);
      console.table(projects);
    }

    // 4. Sample verification: Check if a specific migrated customer exists
    if (mappings && mappings.length > 0) {
      const sampleMapping = mappings[0];
      console.log(`📊 Verifying sample customer: ${sampleMapping.customer_name}`);
      
      const { data: customer, error: customerError } = await directSupabase
        .from('customers')
        .select('*')
        .eq('id', sampleMapping.jarvis_customer_id)
        .single();
      
      if (customerError) {
        console.error(`❌ Error fetching customer ${sampleMapping.customer_name}:`, customerError);
      } else {
        console.log(`✅ Customer ${sampleMapping.customer_name} found in JARVIS database:`);
        console.table([customer]);
      }
    }

    // 5. Check Edge Function response
    console.log('📊 Testing Edge Function...');
    try {
      const { data: { session } } = await directSupabase.auth.getSession();
      
      if (!session?.access_token) {
        console.log('⚠️ No active session - Edge Function test skipped');
        console.log('💡 Edge Function requires authentication to work properly');
      } else {
        const response = await fetch('https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/client-customers', {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log(`✅ Edge Function returned ${data.data?.length || 0} customers`);
          console.log('Sample customers from Edge Function:');
          console.table(data.data?.slice(0, 5));
        } else {
          console.error('❌ Edge Function error:', response.status, response.statusText);
          const errorText = await response.text();
          console.error('Error details:', errorText);
        }
      }
    } catch (error) {
      console.error('❌ Error calling Edge Function:', error);
    }

    console.log('✅ Migration verification completed!');
    
    return {
      success: true,
      mappingCount: mappings?.length || 0,
      customerCount,
      projectsWithCustomers: projects?.length || 0
    };
    
  } catch (error) {
    console.error('❌ Migration verification failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// Helper function to run from browser console
(window as any).verifyMigration = verifyCustomerMigration;
